import { arePortTypesCompatible } from '../graph/nodeDefs.js';
import { nodeDefsForKind } from '../graph/nodeRegistry.js';
import type { AppState } from '../state/appState.js';

interface Point {
  x: number;
  y: number;
}

export interface EdgeRef {
  fromNode: string;
  fromPort: number;
  toNode: string;
  toPort: number;
}

export interface PendingConnection {
  fromNode: string | null;
  fromPort: number | null;
  toNode: string | null;
  toPort: number | null;
  restoreEdge?: EdgeRef;
  from: Point;
  current: Point;
  isFromOut: boolean;
  sticky?: boolean;
  restoreOnCancel?: boolean;
  suppressMouseUp?: boolean;
  ignoreNextClick?: boolean;
  startScreen?: Point;
}

interface ConnectPendingResult {
  connected: boolean;
  typeMismatch: boolean;
}

export interface EditorConnectionMutations {
  resolvePendingConnection(
    nodeId: string,
    portIdx: number,
    io: 'in' | 'out',
    pendingConn: PendingConnection | null,
    clearPending: () => void,
  ): ConnectPendingResult;
  reconnectFromInput(
    nodeId: string,
    portIdx: number,
    getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
  ): PendingConnection | null;
  reconnectFromOutput(
    edge: EdgeRef,
    getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
  ): PendingConnection;
  disconnectEdgeToPending(
    edge: EdgeRef,
    clickPos: Point,
    getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
  ): PendingConnection | null;
  cutEdgesWithHistory(edges: EdgeRef[]): number;
}

export interface ConnectionMutationCommands {
  connectEdge: (edge: EdgeRef, isReconnect?: boolean) => void;
  reconnectEdge: (fromEdge: EdgeRef, toEdge: EdgeRef) => void;
  disconnectEdges: (edges: EdgeRef[]) => void;
}

function sameEdge(a: EdgeRef, b: EdgeRef): boolean {
  return a.fromNode === b.fromNode &&
    a.fromPort === b.fromPort &&
    a.toNode === b.toNode &&
    a.toPort === b.toPort;
}

function companionEdgesFor(appState: AppState, edge: EdgeRef): EdgeRef[] {
  const fromNode = appState.nodes.find(n => n.id === edge.fromNode);
  const toNode = appState.nodes.find(n => n.id === edge.toNode);
  if (!fromNode || !toNode) return [];

  const defs = nodeDefsForKind(appState.editorGraphKind);
  const fromDef = defs[fromNode.type];
  const toDef = defs[toNode.type];
  if (!fromDef || !toDef) return [];

  const fromOutputs = typeof fromDef.outputs === 'function' ? fromDef.outputs(fromNode) : fromDef.outputs;
  const toInputs = typeof toDef.inputs === 'function' ? toDef.inputs(toNode) : toDef.inputs;
  const companions: EdgeRef[] = [];

  const boundsOutPort = edge.fromPort + 1;
  const boundsInPort = edge.toPort + 1;

  if (
    fromOutputs[boundsOutPort]?.type === 'bounds' &&
    toInputs[boundsInPort]?.type === 'bounds' &&
    !appState.edges.some(e => e.toNode === edge.toNode && e.toPort === boundsInPort)
  ) {
    companions.push({ fromNode: edge.fromNode, fromPort: boundsOutPort, toNode: edge.toNode, toPort: boundsInPort });
  }

  const coverageOutPort = fromOutputs.findIndex(output => output.name === 'coverage' && output.type === 'float');
  const coverageInPort = toInputs.findIndex(input => input.name === 'coverage' && input.type === 'float');
  if (
    edge.toPort === 0 &&
    coverageOutPort >= 0 &&
    coverageInPort >= 0 &&
    !appState.edges.some(e => e.toNode === edge.toNode && e.toPort === coverageInPort)
  ) {
    companions.push({ fromNode: edge.fromNode, fromPort: coverageOutPort, toNode: edge.toNode, toPort: coverageInPort });
  }

  return companions;
}

function isTypeCompatibleFor(
  appState: AppState,
  outNodeId: string,
  outPortIdx: number,
  inNodeId: string,
  inPortIdx: number,
): boolean {
  const outNode = appState.nodes.find(node => node.id === outNodeId);
  const inNode = appState.nodes.find(node => node.id === inNodeId);
  if (!outNode || !inNode) return true;

  const defs = nodeDefsForKind(appState.editorGraphKind);
  const outDef = defs[outNode.type];
  const inDef = defs[inNode.type];
  if (!outDef || !inDef) return true;

  const outOutputs = typeof outDef.outputs === 'function' ? outDef.outputs(outNode) : outDef.outputs;
  const inInputs = typeof inDef.inputs === 'function' ? inDef.inputs(inNode) : inDef.inputs;
  if (!outOutputs[outPortIdx] || !inInputs[inPortIdx]) return false;
  const outType = outOutputs[outPortIdx]?.type;
  const inType = inInputs[inPortIdx]?.type;
  const emptyDataOutput = (
    appState.editorGraphKind === 'data'
    && inNode.type === 'Output'
    && !appState.edges.some(edge => edge.toNode === inNodeId && edge.toPort === inPortIdx)
  );
  if (emptyDataOutput) return true;
  return !(outType && inType && !arePortTypesCompatible(outType, inType, inNode.type));
}

export function resolvePendingConnectionFor(
  appState: AppState,
  commands: ConnectionMutationCommands,
  nodeId: string,
  portIdx: number,
  io: 'in' | 'out',
  pendingConn: PendingConnection | null,
  clearPending: () => void
): ConnectPendingResult {
  if (!pendingConn) return { connected: false, typeMismatch: false };

  const outNodeId = pendingConn.isFromOut ? pendingConn.fromNode! : nodeId;
  const outPortIdx = pendingConn.isFromOut ? pendingConn.fromPort! : portIdx;
  const inNodeId = pendingConn.isFromOut ? nodeId : pendingConn.toNode!;
  const inPortIdx = pendingConn.isFromOut ? portIdx : pendingConn.toPort!;

  if (!isTypeCompatibleFor(appState, outNodeId, outPortIdx, inNodeId, inPortIdx)) {
    clearPending();
    return { connected: false, typeMismatch: true };
  }

  const finalEdge: EdgeRef = {
    fromNode: outNodeId,
    fromPort: outPortIdx,
    toNode: inNodeId,
    toPort: inPortIdx
  };

  if (pendingConn.restoreEdge && sameEdge(pendingConn.restoreEdge, finalEdge)) {
    clearPending();
    return { connected: true, typeMismatch: false };
  }

  if (pendingConn.isFromOut && io === 'in') {
    if (pendingConn.restoreEdge) commands.reconnectEdge(pendingConn.restoreEdge, finalEdge);
    else {
      commands.connectEdge(finalEdge);
      for (const companion of companionEdgesFor(appState, finalEdge)) commands.connectEdge(companion);
    }
    clearPending();
    return { connected: true, typeMismatch: false };
  }

  if (!pendingConn.isFromOut && io === 'out') {
    if (pendingConn.restoreEdge) commands.reconnectEdge(pendingConn.restoreEdge, finalEdge);
    else {
      commands.connectEdge(finalEdge);
      for (const companion of companionEdgesFor(appState, finalEdge)) commands.connectEdge(companion);
    }
    clearPending();
    return { connected: true, typeMismatch: false };
  }

  return { connected: false, typeMismatch: false };
}

export function reconnectFromInputFor(
  appState: AppState,
  nodeId: string,
  portIdx: number,
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
): PendingConnection | null {
  const existingIdx = appState.edges.findIndex(edge => edge.toNode === nodeId && edge.toPort === portIdx);
  if (existingIdx === -1) return null;

  const existing = appState.edges[existingIdx];
  const anchorPos = getDotWorldPos(existing.fromNode, existing.fromPort, true);

  return {
    fromNode: existing.fromNode,
    fromPort: existing.fromPort,
    toNode: null,
    toPort: null,
    restoreEdge: { ...existing },
    from: anchorPos,
    current: getDotWorldPos(nodeId, portIdx, false),
    isFromOut: true,
    sticky: true,
    restoreOnCancel: true
  };
}

export function reconnectFromOutputFor(
  appState: AppState,
  edge: EdgeRef,
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
): PendingConnection {
  const existingIdx = appState.edges.findIndex(candidate =>
    candidate.fromNode === edge.fromNode &&
    candidate.fromPort === edge.fromPort &&
    candidate.toNode === edge.toNode &&
    candidate.toPort === edge.toPort
  );
  if (existingIdx === -1) {
    return {
      fromNode: null,
      fromPort: null,
      toNode: edge.toNode,
      toPort: edge.toPort,
      restoreEdge: { ...edge },
      from: getDotWorldPos(edge.toNode, edge.toPort, false),
      current: getDotWorldPos(edge.fromNode, edge.fromPort, true),
      isFromOut: false,
      sticky: true,
      restoreOnCancel: true
    };
  }
  const anchorPos = getDotWorldPos(edge.toNode, edge.toPort, false);

  return {
    fromNode: null,
    fromPort: null,
    toNode: edge.toNode,
    toPort: edge.toPort,
    restoreEdge: { ...edge },
    from: anchorPos,
    current: getDotWorldPos(edge.fromNode, edge.fromPort, true),
    isFromOut: false,
    sticky: true,
    restoreOnCancel: true
  };
}

export function disconnectEdgeToPendingFor(
  appState: AppState,
  edge: EdgeRef,
  clickPos: Point,
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
): PendingConnection | null {
  const idx = appState.edges.findIndex(candidate =>
    candidate.fromNode === edge.fromNode &&
    candidate.fromPort === edge.fromPort &&
    candidate.toNode === edge.toNode &&
    candidate.toPort === edge.toPort
  );
  if (idx === -1) return null;

  const fromPos = getDotWorldPos(edge.fromNode, edge.fromPort, true);
  const toPos = getDotWorldPos(edge.toNode, edge.toPort, false);
  const distToFrom = Math.hypot(clickPos.x - fromPos.x, clickPos.y - fromPos.y);
  const distToTo = Math.hypot(clickPos.x - toPos.x, clickPos.y - toPos.y);
  const detachInputSide = distToTo <= distToFrom;

  if (detachInputSide) {
    return {
      fromNode: edge.fromNode,
      fromPort: edge.fromPort,
      toNode: null,
      toPort: null,
      restoreEdge: { ...edge },
      from: fromPos,
      current: clickPos,
      isFromOut: true,
      sticky: true,
      restoreOnCancel: true
    };
  }

  return {
    fromNode: null,
    fromPort: null,
    toNode: edge.toNode,
    toPort: edge.toPort,
    restoreEdge: { ...edge },
    from: toPos,
    current: clickPos,
    isFromOut: false,
    sticky: true,
    restoreOnCancel: true
  };
}

export function cutEdgesWithHistoryFor(commands: Pick<ConnectionMutationCommands, 'disconnectEdges'>, edges: EdgeRef[]): number {
  if (edges.length === 0) return 0;
  commands.disconnectEdges(edges);
  return edges.length;
}
